import { gCurrentContextPromptSeriesID, kROUser } from "../constants";
import { QBoxLayout } from "./QBoxLayout";
import { QStream } from "./QStream.js";
import { QChartTemplatesMgr } from "./QChartTemplatesMgr";
import { QXMLLoader } from "./QXMLLoader";
import { Globals } from "./Globals";

export class QUser {
  constructor() {
    this.fUserName = null;
    this.fHashPass = null;
    this.fOrgName = null;
    this.fUserEmail = null;
    this.fMobile = null;
    this.fStatus = null;
    this.fUserType = null;
    this.fGroupID = null;

    this.fPrevUserID = null;
    this.fCanExportExcel = true;
    this.fCanUpdateTemplates = true;
    this.fCanPIT = true;
    this.fCanReport = true;
    this.fOptIn = false;
    this.fPointInTime = null;
    this.fLoadFromPointInTime = null;
    this.fChartRules = {};

    this.fLanguage = "en";

    this.fDelegatedEmail = null;
    this.fIsDelegated = false;
    this.fMFA = {};
    this.fMFAState = null;
    this.fMFAStateLabel = null; // The label form of MFA State to prevent duplication of fns.
    this.fReadMessages = [];
    this.fUserSessionInfo = {};
    this.fHasIFH = true;
    this.fSignOutURL = null;
    this.fSignOutOverride = null;

    this.fUserColor = null;

    this.fChartGroups = [];
  }

  Clone() {
    var user = new QUser();
    var userInfo = JSON.parse(JSON.stringify(this));
    var keys = Object.keys(userInfo);
    for (var aKeyIdx in keys) {
      var theKey = keys[aKeyIdx];
      user[theKey] = userInfo[theKey];
    }
    return user;
  }

  GetName() {
    return this.fName;
  }

  SetName(name) {
    this.fName = name;
  }

  GetPointInTime() {
    return this.fPointInTime;
  }

  GetLoadFromPointInTime() {
    return this.fLoadFromPointInTime;
  }

  SetPointInTime(newDate) {
    this.fPointInTime = newDate;
  }

  GetUserName() {
    return this.fUserName;
  }

  GetMFA(attr) {
    var mfa = this.fMFA || {};
    return attr != null ? mfa[attr] : mfa;
  }

  SetMFA(wsMgr, mfa, reset) {
    this.fMFA = reset ? {} : { ...this.fMFA, ...mfa };
    if (mfa["backendMFAState"] != null) this.SetMFAState(wsMgr, this);
  }

  GetMFAState() {
    return this.fMFAState;
  }

  GetMFAStateLabel() {
    return this.fMFAStateLabel;
  }

  SetMFAState(wsMgr, user) {
    this.fMFAState = wsMgr.GetMFAState(user, true);
    this.fMFAStateLabel = wsMgr.GetAccessGroupMgr().GetMFALabel(this.fMFAState);
  }

  GetUserSessionInfo() {
    return this.fUserSessionInfo;
  }

  SetUserSessionInfo(userSessionInfo) {
    this.fUserSessionInfo = userSessionInfo;
  }

  SetFavoriteInfo(userFavoriteInfo) {
    this.fUserFavoriteInfo = userFavoriteInfo;
  }

  GetFavoriteInfo() {
    return this.fUserFavoriteInfo;
  }

  GetReadContextPrompts() {
    var userSessionInfo = this.GetUserSessionInfo();
    var readContextPrompts = [];
    if (userSessionInfo && userSessionInfo["contextPrompts"])
      readContextPrompts = userSessionInfo["contextPrompts"];
    return readContextPrompts;
  }

  HasSeenCurrentContextPrompt() {
    var viewedContextPrompts = this.GetReadContextPrompts();
    return viewedContextPrompts.includes(gCurrentContextPromptSeriesID);
  }

  SetUserName(userName) {
    this.fUserName = userName;
  }

  GetAccountName() {
    return this.fOrgName;
  }

  GetUserEmail() {
    return this.fUserEmail;
  }

  SetUserEmail(userEmail) {
    this.fUserEmail = userEmail;
  }

  GetMobile() {
    return this.fMobile;
  }

  SetMobile(mobile) {
    this.fMobile = mobile;
  }

  SetPassword(password) {
    this.fHashPass = password;
  }

  GetPassword(password) {
    return this.fHashPass;
  }

  GetStatus() {
    return this.fStatus;
  }

  SetStatus(newStatus) {
    this.fStatus = newStatus;
  }

  GetUserRole() {
    return this.fUserType;
  }

  SetUserType(userType) {
    this.fUserType = userType;
  }

  GetPrevUserID() {
    return this.fPrevUserID;
  }

  SetPrevUserID(prevID) {
    this.fPrevUserID = prevID;
  }

  SetPrevEmail(prevEmail) {
    this.fPrevEmail = prevEmail;
  }

  GetPrevEmail() {
    return this.fPrevEmail;
  }

  GetUserGroupID() {
    return this.fGroupID;
  }

  SetUserGroupID(groupID) {
    this.fGroupID = groupID;
  }

  GetCanPIT() {
    return this.fCanPIT;
  }

  SetCanPIT(canPIT) {
    this.fCanPIT = canPIT;
  }

  GetCanReport() {
    return this.fCanReport;
  }

  SetCanReport(canReport) {
    this.fCanReport = canReport;
  }

  CanExportExcel() {
    return this.fCanExportExcel;
  }

  CanExportGoogleSlides() {
    return this.fCanExportGoogleSlides;
  }

  SetCanExportExcel(canExport) {
    this.fCanExportExcel = canExport;
  }

  SetCanExportGoogleSlides(canExport) {
    this.fCanExportGoogleSlides = canExport;
  }

  CanUpdateTemplates() {
    return this.fCanUpdateTemplates && this.GetUserRole() !== kROUser;
  }

  SetCanUpdateTemplates(canUpdate) {
    this.fCanUpdateTemplates = canUpdate;
  }

  HasIFH() {
    return this.fHasIFH;
  }

  SetHasIFH(IF) {
    this.fHasIFH = IF;
  }

  GetOptIn() {
    return this.fOptIn;
  }

  SetOptIn(optin) {
    this.fOptIn = optin;
  }

  SetReadMessages(messageIds) {
    this.fReadMessages = messageIds;
  }

  GetReadMessages() {
    return this.fReadMessages;
  }

  MarkMessageRead(messageId) {
    if (!this.fReadMessages.includes(messageId)) {
      this.fReadMessages.push(messageId);
    }
  }

  GetIsDelegated() {
    return this.fIsDelegated;
  }

  SetIsDelegated(bool) {
    this.fIsDelegated = bool;
  }

  GetChartRules() {
    return this.fChartRules;
  }

  SetChartRules(rules) {
    this.fChartRules = rules;
  }

  GetSignOutURL() {
    var signoutOverride = this.GetSignOutOverride();
    return signoutOverride != null ? signoutOverride : this.fSignOutURL;
  }

  SetSignOutURL(url) {
    this.fSignOutURL = url;
  }

  SetSignOutOverride(url) {
    this.fSignOutOverride = url;
  }

  GetSignOutOverride() {
    return this.fSignOutOverride;
  }

  SetRecentSearches(recentSearches) {
    this.fRecentSearches = recentSearches;
  }

  GetRecentSearches() {
    return this.fRecentSearches;
  }

  GetRecentChartSearches(chartID) {
    let recentSearches = this.fRecentSearches;
    if (!recentSearches) return [];
    return this.fRecentSearches[chartID] ?? [];
  }

  GetChartGroups() {
    return this.fChartGroups;
  }

  GetChartGroupByID(groupID) {
    return this.fChartGroups.find(group => group.id === groupID);
  }

  SetLastActiveGroup(groupID) {
    this.fLastActiveGroup = groupID;
  }

  GetLastActiveGroup() {
    return this.fLastActiveGroup;
  }

  // update fxns
  UpdateUserValue(wsMgr, aKey, userDict) {
    switch (aKey) {
      case "name":
        this.fName = userDict[aKey];
        break;
      case "username":
        this.fUserName = userDict[aKey];
        break;
      case "email":
        this.fUserEmail = userDict[aKey];
        break;
      case "org":
        this.fOrgName = userDict[aKey];
        break;
      case "group":
        this.fGroupID = userDict[aKey];
        break;
      case "role":
        this.fUserType = userDict[aKey];
        Globals._accountInfo.SetUserType(this.fUserType);
        break;
      case "mfa":
        this.SetMFA(wsMgr, userDict[aKey]);
        break;
      case "recent":
        this.SetRecentSearches(userDict[aKey]);
        break;
      case "chartRules":
        this.fChartRules = userDict[aKey];
        break;
      case "usertemplates":
        var theList = userDict[aKey];
        if (theList.length > 0) {
          var chartTemplatesMgr = wsMgr.GetChartTemplatesMgr();
          if (chartTemplatesMgr == null) {
            chartTemplatesMgr = new QChartTemplatesMgr();
            chartTemplatesMgr.InitChartTemplatesMgr(wsMgr);
          }
          wsMgr.fChartTemplateMgr = chartTemplatesMgr;
          if (theList[0].fTemplateName != null) {
            //Purposefully not handling this for now.
            chartTemplatesMgr.LoadLegacyUserTemplateSet();
          } else chartTemplatesMgr.LoadTemplateSet(theList, chartTemplatesMgr.fUserTemplates);
        }
        break;
      case "userboxstyles":
        var boxStylesMgr = wsMgr.GetBoxStylesMgr();
        var boxStylesData = userDict[aKey];
        try {
          if (typeof boxStylesData == "string") {
            boxStylesMgr.fUserBoxStyles = [];
            var s = new QStream();
            s.InitStreamFromXML(boxStylesData);
            var loader = new QXMLLoader();
            var objTree = loader.LoadXML(s);
            var PUID = objTree.GetValueForChild("PUID", true);
            if (PUID) boxStylesMgr.fPUID = objTree.GetValueForChild("PUID", true);
            var childList = objTree.GetChildOfTypeList("boxlayout");
            var numLayouts = childList.length;
            for (var i = 0; i < numLayouts; i++) {
              var layoutInfo = childList[i];
              var aLayout = new QBoxLayout();
              aLayout.LoadFromXML(layoutInfo);
              aLayout.FinishLoadBoxStyle();
              boxStylesMgr.fUserBoxStyles.push(aLayout);
            }
          } else {
            boxStylesMgr.LoadFromJSON({ user: boxStylesData });
          }
        } catch (err) {
          console.error(err);
        }
        break;
      case "filters":
        this.fUserFilters = userDict[aKey];
        break;
      case "language":
        this.fLanguage = userDict[aKey];
        localStorage.language = this.fLanguage;
        Globals._stringMgr.ResetStringMgr();
        break;
      case "pointintime":
        this.fPointInTime = userDict[aKey];
        break;
      case "optin":
        this.fOptIn = userDict["optin"];
        break;
      case "delegatedEmail":
        this.fDelegatedEmail = userDict["delegatedEmail"];
        this.SetIsDelegated(true);
        break;
      case "readMessages":
        this.fReadMessages = userDict["readMessages"];
        break;
      case "sessionInfo":
        this.SetUserSessionInfo(userDict["sessionInfo"]);
        break;
      case "favorites":
        this.SetFavoriteInfo(userDict["favorites"]);
        break;
      case "chartGroups":
        this.fChartGroups = userDict[aKey];
        break;
    }
  }

  UpdateUserGroupInfo(groupInfo) {
    var signOutObj = groupInfo != null ? groupInfo["signout"] || {} : {}; // They're located in diff. depths b/c one of them is just temporary.
    if (signOutObj["customSignOut"] || signOutObj["signOutURLOverride"]) {
      this.SetSignOutOverride(signOutObj["signOutURLOverride"]);
      this.SetSignOutURL(signOutObj["signOutURL"]);
    }

    if ("groups" in groupInfo) groupInfo = groupInfo["groups"];
    this.SetCanExportExcel(false);
    this.SetCanUpdateTemplates(false);

    if (groupInfo[this.fGroupID]) {
      //TODO: Access group should really be an object at this point at the user object is attached to
      var myGroup = groupInfo[this.fGroupID];
      this.SetCanExportExcel(myGroup["canExportExcel"]);
      this.SetCanUpdateTemplates(myGroup["canEditTemplates"]);
      this.SetHasIFH(myGroup["interactiveFiltering"]);
      this.SetCanPIT(myGroup["pointintime"]);
      this.SetCanReport(myGroup["reporting"]);
    } else if (this.fGroupID == "0") {
      this.SetCanExportExcel(true);
      this.SetCanExportGoogleSlides(true);
      this.SetCanUpdateTemplates(true);
    }
  }
}
